// src/components/learners/ProfileDrawer.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Camera, Loader2, UserRound, User, CircleUser, Ellipsis, Briefcase, Globe, Phone, Cake, Hash, Link, Check } from "lucide-react";
import Select from "react-select";
import { useKuizyTheme } from "@/styles/kuizyTheme";
import { updateLearnerProfile, uploadLearnerAvatar, getPrefixes, getCountries } from "@/api/kuizy";
import { getSocialGoogle, authGoogle, disconnectGoogle } from '@/api/auth'
import { useGoogleLogin } from '@react-oauth/google'
import ConfirmModal from "@/widgets/ConfirmModal"

const pop = { initial: { opacity: 0, x: 24 }, animate: { opacity: 1, x: 0 }, transition: { duration: .22 } };

const makeSelectStyles = (isDark) => ({
  control: (base, state) => ({
    ...base,
    backgroundColor: isDark ? "#0b0f19" : "#ffffff",
    borderColor: state.isFocused ? (isDark ? "rgba(255,255,255,.35)" : "#60a5fa") : (isDark ? "rgba(255,255,255,.15)" : "#d1d5db"),
    boxShadow: "none",
    minHeight: 44,
    ":hover": { borderColor: isDark ? "rgba(255,255,255,.35)" : "#9ca3af" }
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1000 }),
  menu: (base) => ({ ...base, backgroundColor: isDark ? "#0b0f19" : "#ffffff", color: isDark ? "#e5e7eb" : "#111827" }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? (isDark ? "#1f2937" : "#e5e7eb") : state.isFocused ? (isDark ? "#111827" : "#f3f4f6") : "transparent",
    color: isDark ? "#e5e7eb" : "#111827",
    cursor: "pointer"
  }),
  singleValue: (base) => ({ ...base, color: isDark ? "#f9fafb" : "#111827" }),
  input: (base) => ({ ...base, color: isDark ? "#f9fafb" : "#111827" }),
  placeholder: (base) => ({ ...base, color: isDark ? "rgba(229,231,235,.55)" : "#6b7280" }),
  multiValue: (base) => ({ ...base, backgroundColor: isDark ? "rgba(255,255,255,.08)" : "#eef2ff" }),
  multiValueLabel: (base) => ({ ...base, color: isDark ? "#e5e7eb" : "#111827" }),
  multiValueRemove: (base) => ({ ...base, color: isDark ? "#e5e7eb" : "#6b7280", ":hover": { backgroundColor: isDark ? "#1f2937" : "#e5e7eb", color: isDark ? "#fff" : "#111827" } }),
});

export default function ProfileDrawer({ open, onClose, profile, onSaved }) {
  const { t, theme } = useKuizyTheme();
  const isDark = theme === "dark";
  const selectStyles = useMemo(() => makeSelectStyles(isDark), [isDark]);

  const [form, setForm] = useState({
    prefix_id: "", job_title: "", first_name: "", last_name: "", nick_name: "",
    birth_year: "", country: "TH", phone_code: "+66", phone: ""
  });
  const [prefixes, setPrefixes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const fileRef = useRef(null);

  const [connectedGoogle, setConnectGoogle] = useState(false)
  const [connectSocial, setConnectSocial] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [delItem, setDelItem] = useState(null)
  const [alert, setAlert] = useState({ open: false, msg: '' })


  const fetchSocials = async () => {
    try {
      const res = await getSocialGoogle()

      if (res?.data?.connected) {
        setConnectGoogle(true)
        setConnectSocial(res.data)
      } else {
        setConnectGoogle(false)
        setConnectSocial(null)
      }
    } catch (err) {
      console.error(err)
      setConnectGoogle(false)
      setConnectSocial(null)
    }
  }
  useEffect(() => {
    fetchSocials()
  }, [])

  const connectGoogle = useGoogleLogin({
    scope: 'openid email profile',
    onSuccess: (res) => {
      authGoogle(res.access_token) // ✅ access_token
      // fetchSocials() 
      if (!res.access_token) return
      setAlert({ open: true, msg: "เชื่อมต่อ Google สำเร็จ" })
    },
    onError: () => {
      setAlert({ open: true, msg: "ไม่สามารถเชื่อมต่อได้" })
    }
  })

  const handleDisconnectGoogle = async () => {
    setDelItem(true)
  }

  const confirmDelete = async () => {
    if (!delItem) return
    setDeleting(true)
    try {
      const res = await disconnectGoogle()
      if (!res.data.success) return setAlert({ open: true, msg: "ไม่สามารถยกเลิกการเชื่อมต่อได้" })
      fetchSocials()
      setAlert({ open: true, msg: "ยกเลิกการเชื่อมต่อ Google สำเร็จ" })
    } catch {
      setAlert({ open: true, msg: "ไม่สามารถลบได้" })
    } finally {
      fetchSocials()
      setDeleting(false)
      setDelItem(null)
    }
  }


  useEffect(() => {
    if (!open) return;
    Promise.all([getPrefixes().catch(() => ({ data: [] })), getCountries().catch(() => ({ data: [] }))]).then(([pf, ct]) => {
      setPrefixes(pf.data || []);
      setCountries(ct.data || []);
    });
    setForm({
      prefix_id: profile?.prefix_id || "",
      job_title: profile?.job_title || "",
      first_name: profile?.first_name || "",
      last_name: profile?.last_name || "",
      nick_name: profile?.nick_name || "",
      birth_year: profile?.birth_year ? String(profile.birth_year) : "",
      country: profile?.country || "TH",
      phone_code: profile?.phone_code || "+66",
      phone: profile?.phone || ""
    });
    setPreview("");
  }, [open, profile]);

  const avatarSrc = useMemo(() => preview || profile?.avatar_url || "", [preview, profile]);

  const pickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    try {
      setUploading(true);
      const r = await uploadLearnerAvatar(f);
      onSaved?.(r?.data);
    } finally {
      setUploading(false);
    }
  };

  const prefixOptions = prefixes.map(p => ({ value: p.id, label: `${p.name_th} (${p.name_en})` }));
  const countryOptions = countries.map(c => ({ value: c.iso2, label: `${c.name_en} (${c.phone_code})`, phone_code: c.phone_code }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const pr = prefixes.find(p => String(p.id) === String(form.prefix_id));
      const prName = pr?.name_th || pr?.name_en || "";
      const left = `${prName}${form.first_name || ""}`.trim();
      let display_name = left;
      if (form.last_name) display_name = `${display_name} ${form.last_name}`;
      if (form.nick_name) display_name = `${display_name} (${form.nick_name})`;
      const payload = {
        prefix_id: form.prefix_id || null,
        job_title: form.job_title || null,
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        nick_name: form.nick_name || null,
        birth_year: form.birth_year || null,
        country: form.country || null,
        phone_code: form.phone_code || null,
        phone: form.phone || null,
        display_name: display_name || null
      };
      await updateLearnerProfile(payload);
      onSaved?.();
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <motion.aside {...pop} className={`absolute right-0 top-0 h-full w-full sm:w-[480px] ${t.bgBase} ${t.text} border-l ${t.border}`}>
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-base font-semibold">
            <UserRound className="size-5" />
            <span>โปรไฟล์ผู้เรียน</span>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-xl grid place-items-center bg-white/10">
            <X className="size-4" />
          </button>
        </div>

        <div className="px-5 pb-5 overflow-y-auto h-[calc(100%-56px)]">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="h-20 w-20 rounded-2xl overflow-hidden bg-white/10 grid place-items-center">
                {avatarSrc ? <img src={avatarSrc} alt="avatar" className="h-20 w-20 object-cover" /> : <UserRound className="size-7 opacity-70" />}
              </div>
              <button onClick={pickFile} className="absolute -bottom-2 -right-2 h-9 w-9 rounded-xl grid place-items-center bg-primary text-white shadow">
                {uploading ? <Loader2 className="size-4 animate-spin" /> : <Camera className="size-4" />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
            </div>
            <div>
              <div className="text-sm opacity-80">อีเมล/ผู้ใช้</div>
              <div className="text-base font-medium">{profile?.username || "-"}</div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <div className={`text-xs mb-1 ${t.subtext} flex items-center gap-1`}><Ellipsis className="size-4" /> คำนำหน้า</div>
              <Select
                classNamePrefix="rs"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={prefixOptions}
                value={prefixOptions.find(o => o.value === form.prefix_id) || null}
                onChange={o => setForm(s => ({ ...s, prefix_id: o?.value || "" }))}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className={`text-xs mb-1 ${t.subtext} flex items-center gap-1`}><User className="size-4" /> ชื่อต้น</div>
                <input className={`w-full h-11 rounded-xl px-3 ${t.input}`} value={form.first_name} onChange={e => setForm(s => ({ ...s, first_name: e.target.value }))} />
              </div>
              <div>
                <div className={`text-xs mb-1 ${t.subtext} flex items-center gap-1`}><CircleUser className="size-4" /> นามสกุล</div>
                <input className={`w-full h-11 rounded-xl px-3 ${t.input}`} value={form.last_name} onChange={e => setForm(s => ({ ...s, last_name: e.target.value }))} />
              </div>
              <div>
                <div className={`text-xs mb-1 ${t.subtext} flex items-center gap-1`}><UserRound className="size-4" /> ชื่อเล่น</div>
                <input className={`w-full h-11 rounded-xl px-3 ${t.input}`} value={form.nick_name} onChange={e => setForm(s => ({ ...s, nick_name: e.target.value }))} />
              </div>
              <div>
                <div className={`text-xs mb-1 ${t.subtext} flex items-center gap-1`}><Cake className="size-4" /> ปีเกิด</div>
                <input className={`w-full h-11 rounded-xl px-3 ${t.input}`} value={form.birth_year} onChange={e => setForm(s => ({ ...s, birth_year: e.target.value.replace(/\D/g, "") }))} />
              </div>
            </div>

            <div>
              <div className={`text-xs mb-1 ${t.subtext} flex items-center gap-1`}><Briefcase className="size-4" /> ตำแหน่ง/บทบาท</div>
              <input className={`w-full h-11 rounded-xl px-3 ${t.input}`} value={form.job_title} onChange={e => setForm(s => ({ ...s, job_title: e.target.value }))} />
            </div>

            <div>
              <div className={`text-xs mb-1 ${t.subtext} flex items-center gap-1`}><Globe className="size-4" /> ประเทศ</div>
              <Select
                classNamePrefix="rs"
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={countryOptions}
                value={countryOptions.find(o => o.value === form.country) || null}
                onChange={o => setForm(s => ({ ...s, country: o?.value || "", phone_code: o?.phone_code ? `+${String(o.phone_code).replace(/^\+?/, "")}` : s.phone_code }))}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
              <div>
                <div className={`text-xs mb-1 ${t.subtext} flex items-center gap-1`}><Hash className="size-4" /> รหัสโทร</div>
                <input className={`w-full h-11 rounded-xl px-3 ${t.input} text-center`} value={form.phone_code} onChange={e => setForm(s => ({ ...s, phone_code: e.target.value }))} />
              </div>
              <div>
                <div className={`text-xs mb-1 ${t.subtext} flex items-center gap-1`}><Phone className="size-4" /> โทรศัพท์</div>
                <input className={`w-full h-11 rounded-xl px-3 ${t.input}`} value={form.phone} onChange={e => setForm(s => ({ ...s, phone: e.target.value.replace(/\D/g, "") }))} />
              </div>
            </div>

            <div className="pt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={onClose} className="h-11 rounded-xl bg-white/10 shadow-lg">ยกเลิก</button>
              <button type="submit" disabled={loading} className={`h-11 rounded-xl ${t.btn}`}>
                {loading ? <Loader2 className="size-4 animate-spin" /> : "บันทึก"}
              </button>
            </div>
          </form>
          <div className="bg-white rounded-2xl shadow-md p-5 mt-3">
            <div className="text-sm font-semibold flex items-center gap-2 mb-2">
              <Link size={16} />
              เชื่อมต่อโซเซียลมิเดีย
            </div>
            <div className="text-sm flex items-center gap-2 mb-2">
              เชื่อมต่อบัญชีโซเชียลมิเดียของคุณเพื่อการเข้าถึงที่สะดวกยิ่งขึ้น
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <img src="../icons/google.svg" alt="google" className="w-6 h-6 mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Google</span>

                  {connectedGoogle && (
                    <span className="text-xs text-gray-500">
                      {connectSocial?.google_email}
                    </span>
                  )}
                </div>
              </div>

              {connectedGoogle ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600 font-medium">
                    <Check size={12} className="inline-block mr-0.5" />
                    เชื่อมต่อแล้ว
                  </span>
                  <button
                    type="button"
                    className="text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
                    onClick={handleDisconnectGoogle}
                  >
                    ยกเลิก
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => connectGoogle()}
                  className="text-sm px-3 py-2 rounded-xl border border-gray-300 hover:bg-gray-50" >
                  เชื่อมต่อ
                </button>
              )}
            </div>
          </div>
        </div>
        {delItem && (
          <ConfirmModal
            type="confirm"
            title="ยืนยันการยกเลิกการเชื่อมต่อ"
            message={`ต้องการยกเลิกการเชื่อมต่อ Google ใช่หรือไม่?`}
            pending={deleting}
            onConfirm={confirmDelete}
            onCancel={() => setDelItem(false)}
          />
        )}

        {alert.open && <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: '' })} />}
      </motion.aside>
    </div>,
    document.body
  );
}
